import getIn from 'lodash/get';
import noop from 'lodash/noop';
import { createSelector } from 'reselect';
import UAParser from 'ua-parser-js';

import {
  BrowserSupportLevels,
  ClientInfoKeys,
  ClientVersionSemverRegex,
  OsNames,
} from '../constants/ClientInfo';
import { PurchaseLinks } from '../constants/Onboarding';
import {
  getShouldAlertOnClientMismatch,
  getSupportedBrowsers,
} from '../lib/configuration/TunerVarUtils';
import { compareVersionNumbers } from '../lib/NumberUtils';

/**
 * ClientInfoUtils
 *
 * @module lib/ClientInfoUtils
 */

export interface IOS {
  name: string | undefined;
  version: string | undefined;
}

export interface IBrowser {
  major: string | undefined;
  name: string | undefined;
  version: string | undefined;
}

// Check the DOM for current client version number. Returns string.
export function getCurrentClientVersion(): string {
  // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
  const version = __VERSION__.match(ClientVersionSemverRegex)[0];
  return version;
}

export function getClientInfo(state: $TSFixMe): $TSFixMe {
  return state.clientInfo;
}

export function getBrowserInfo(): IBrowser {
  const UserAgent = new UAParser();
  const Browser = UserAgent.getBrowser();
  return Browser;
}

export function getBrowserVersion(): string | undefined {
  const info = getBrowserInfo();
  return info && info.version;
}

export function getOSInfo(): IOS {
  const UserAgent = new UAParser();
  const Os = UserAgent.getOS();
  return Os;
}

export function isWindowsClient(): boolean {
  const Os = getOSInfo();
  return Os.name === OsNames.WINDOWS;
}

export function isAndroidClient(): boolean {
  const Os = getOSInfo();
  return Os.name === OsNames.ANDROID;
}

export function isIOSClient(): boolean {
  const Os = getOSInfo();
  return Os.name === OsNames.IOS;
}

// @todo - WEBCLIENT-6923 - Move 'PurchaseLinks' so that this map can then go in a constants file w/o having an awkward dependency
const mapOsNameToNativeLink = {
  [OsNames.ANDROID]: PurchaseLinks.GOOGLE,
  [OsNames.IOS]: PurchaseLinks.ITUNES,
};

export function getNativeLinkByOS(OSInfo: IOS): string {
  // @ts-expect-error TS(2538) FIXME: Type 'undefined' cannot be used as an index type.
  return mapOsNameToNativeLink[OSInfo.name];
}

export const getDeviceStoreUrl = createSelector(getOSInfo, getNativeLinkByOS);

export function getBrowserSupport(state: $TSFixMe): string | undefined {
  const info = getClientInfo(state);
  return info?.get(ClientInfoKeys.BROWSER_SUPPORT);
}

/* getBrowserSupportLevel
 *  browser: object containing name and version of browser to check
 *  returns: one of the BrowserSupportLevel properties, none, basic, or full
 */
export function calculateBrowserSupportLevel(state: $TSFixMe, currentBrowser: IBrowser): string {
  const supportedBrowsers = getSupportedBrowsers(state);

  if (!supportedBrowsers || !supportedBrowsers.size || !supportedBrowsers.find) {
    return BrowserSupportLevels.FULL;
  }

  const browser = supportedBrowsers.find((val: $TSFixMe) => {
    return val.name === currentBrowser.name;
  });

  if (!browser || !browser.versions || !currentBrowser.version) {
    return BrowserSupportLevels.FULL;
  }

  if (compareVersionNumbers(browser.versions.minimum, currentBrowser.version) > 0) {
    return BrowserSupportLevels.NONE;
  }

  if (compareVersionNumbers(browser.versions.recommended, currentBrowser.version) > 0) {
    return BrowserSupportLevels.BASIC;
  }

  return BrowserSupportLevels.FULL;
}

export function getDeviceOS(state: $TSFixMe): string {
  const info = getClientInfo(state);

  const browserName = info.get(ClientInfoKeys.BROWSER_NAME);
  const browserVersion = info.get(ClientInfoKeys.BROWSER_VERSION);
  const osName = info.get(ClientInfoKeys.OS_NAME);

  return `${browserName} ${browserVersion} (${osName})`;
}

export function getOSName(state: $TSFixMe): string {
  const info = getClientInfo(state);
  return info.get(ClientInfoKeys.OS_NAME);
}

export function isElectron(): boolean {
  return !!(window.isElectron && window.isElectron());
}

export function getDeviceUuidIfMetWindowsDesktopAppAccountMigrationRequirement(): string | null {
  const isDesktopApp = isElectron();
  const isWindows = isWindowsClient();

  if (isDesktopApp && isWindows) {
    // Only check further if it's from Windows Desktop app
    const isWindowsAccountMigrated = getIn(
      window,
      'DesktopBridge.isWindowsAccountMigrated',
      noop,
    )();
    const deviceUuid = getIn(window, 'DesktopBridge.getDeviceUuid', noop)();
    if (!isWindowsAccountMigrated && deviceUuid) {
      return deviceUuid;
    }
  }

  return null;
}

/*
 * Returns a string containing the browser name, version, and os name. Truncates version
 * to nearest major version for better sorting. Example "Chrome 54 (Mac OS)"
 */
export function getSimplifiedDeviceOS(state: $TSFixMe): string {
  const info = getClientInfo(state);

  const browserName = info.get(ClientInfoKeys.BROWSER_NAME);
  const osName = info.get(ClientInfoKeys.OS_NAME);
  let browserVersion = info.get(ClientInfoKeys.BROWSER_VERSION);
  if (browserVersion) {
    browserVersion = browserVersion.split('.')[0];
  }

  return `${browserName} ${browserVersion} (${osName})`;
}

/*
 * If RTC WEB_ALERT_ON_CLIENT_VERSION_MISMATCH is true, compares the users currently running client version with the most
 * recently released webclient version.
 * Returns true if the user version is too high or low, and false if the versions match or if the RTC is false.
 */
export function shouldPromptUserReload(state: $TSFixMe, mostRecentVersion: $TSFixMe): boolean {
  //  If RTC is on, check if user client version is out of date
  if (getShouldAlertOnClientMismatch(state)) {
    return compareVersionNumbers(getCurrentClientVersion(), mostRecentVersion) !== 0;
  }
  return false;
}
